import type { Measurement } from "../../measure/measureSession";
import type {
  BabylonState,
  MeasureOverlayPoint,
  MeasureOverlayProjection,
  Vector3Plain,
} from "../../state";

import { projectDataPoint } from "./projectDataPoint";

/**
 * Projected positions closer than this count as unmoved, so sub-pixel
 * camera drift does not trigger a React update every frame.
 */
const SCREEN_EPSILON_PX = 0.5;

/**
 * Reports the measure overlay's screen positions to React, at most once
 * per change.
 *
 * Runs every frame after `scene.render`, projecting through the matrices
 * that frame used so the positions match the pixels. It reports only when
 * something moved by at least half a pixel, appeared, or disappeared.
 * The overlay reflects measure state, not tool mode: completed
 * measurements stay labelled after the tool is toggled off, just as
 * their lines stay drawn.
 */
export function reportMeasureOverlay(state: BabylonState): void {
  const tool = state.input.tool;
  const onMeasureOverlay = state.context.callbacks.onMeasureOverlay;
  if (!tool || !onMeasureOverlay) {
    return;
  }

  const camera = state.sceneConfig.camera;
  const measure = tool.measure;
  const hasContent =
    measure !== undefined &&
    (measure.session.phase === "anchored" || measure.measurements.length > 0);

  let overlay: MeasureOverlayProjection | null = null;
  if (camera && hasContent) {
    overlay = {
      labels: measure.measurements.map((measurement) => ({
        id: measurement.id,
        lengthMeters: measurement.length,
        point: projectDataPoint(state, camera, midpointOf(measurement)),
      })),
    };
    if (measure.session.phase === "anchored") {
      overlay.anchor = projectDataPoint(state, camera, measure.session.start);
      if (tool.mode === "measure" && tool.pointer) {
        overlay.cursor = {
          screenX: tool.pointer.x,
          screenY: tool.pointer.y,
        };
      }
    }
  }

  const previous = tool.reportedMeasureOverlay ?? null;
  if (overlayEquals(previous, overlay)) {
    return;
  }
  tool.reportedMeasureOverlay = overlay;
  onMeasureOverlay(overlay);
}

function midpointOf(measurement: Measurement): Vector3Plain {
  return {
    x: (measurement.start.x + measurement.end.x) / 2,
    y: (measurement.start.y + measurement.end.y) / 2,
    z: (measurement.start.z + measurement.end.z) / 2,
  };
}

function overlayEquals(
  a: MeasureOverlayProjection | null,
  b: MeasureOverlayProjection | null,
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    optionalEquals(a.anchor, b.anchor, pointEquals) &&
    optionalEquals(a.cursor, b.cursor, cursorEquals) &&
    a.labels.length === b.labels.length &&
    a.labels.every((label, i) => {
      const other = b.labels[i];
      return (
        label.id === other.id &&
        label.lengthMeters === other.lengthMeters &&
        pointEquals(label.point, other.point)
      );
    })
  );
}

/** Both absent, or both present and equal under `equals`. */
function optionalEquals<T>(
  a: T | undefined,
  b: T | undefined,
  equals: (left: T, right: T) => boolean,
): boolean {
  if (a === undefined || b === undefined) {
    return a === b;
  }
  return equals(a, b);
}

function pointEquals(a: MeasureOverlayPoint, b: MeasureOverlayPoint): boolean {
  return (
    a.visible === b.visible &&
    Math.abs(a.screenX - b.screenX) < SCREEN_EPSILON_PX &&
    Math.abs(a.screenY - b.screenY) < SCREEN_EPSILON_PX
  );
}

/**
 * Exact, unlike `pointEquals`: the cursor is a browser-reported pointer
 * position, not a projection, so it has no drift to absorb.
 */
function cursorEquals(
  a: { screenX: number; screenY: number },
  b: { screenX: number; screenY: number },
): boolean {
  return a.screenX === b.screenX && a.screenY === b.screenY;
}
